//src/utils/firestore/FirestorePaths.ts

import { db } from '@/firebase'
import { AccountConverter } from '@/types/Account'
import { doc } from 'firebase/firestore'

export enum FirebaseCollections {
  Stores = 'StoresCustomers',
  Aircraft = 'Aircraft',
  Events = 'Events',
  Rentals = 'Rentals',
  Rooms = 'Rooms',
  Leads = 'Leads',
  Equipment = 'Equipment',
  Finance = 'Finance',
  Client = 'Clients',
  Accounts = 'Accounts',
  Documents = 'Documents',
  Maintenances = 'Maintenances',
  Logbook = 'Logbook',
  Courses = 'Courses',
  Alerts = 'Alerts',
  AlertReceipts = 'AlertReceipts',
  Invoices = 'Invoices',
  ReportTemplates = 'ReportTemplates',
  InspectionEndorsements = 'InspectionEndorsements',
  WorkTime = 'WorkTime',
  /** A school's maintenance area: StoresCustomers/{school}/Maintenance/… */
  Maintenance = 'Maintenance',
  /** The template documents under Maintenance/InspectionEndorsements. */
  Templates = 'Templates'
}

export const FirestorePaths = {
  rootCollection(collection: FirebaseCollections) {
    return collection
  },

  rootDocument(collection: FirebaseCollections, documentId: string) {
    return `${collection}/${documentId}`
  },

  storeRoot() {
    return FirebaseCollections.Stores
  },

  store(storeId: string) {
    return `${FirebaseCollections.Stores}/${storeId}`
  },

  storeCollection(storeId: string, collection: FirebaseCollections) {
    return `${this.store(storeId)}/${collection}`
  },

  storeDocument(storeId: string, collection: FirebaseCollections, documentId: string) {
    return `${this.storeCollection(storeId, collection)}/${documentId}`
  },

  nestedCollection(documentPath: string, collection: FirebaseCollections) {
    return `${documentPath}/${collection}`
  },

  nestedDocument(documentPath: string, collection: FirebaseCollections, documentId: string) {
    return `${documentPath}/${collection}/${documentId}`
  },

  /**
   * A school's maintenance area — the collection that holds maintenance-wide
   * records as named documents, each with its own subcollection:
   *   StoresCustomers/{school}/Maintenance/InspectionEndorsements/Templates/{id}
   *   StoresCustomers/{school}/Maintenance/AuditLog/Entries/{id}  (server-written)
   */
  maintenanceArea(storeId: string) {
    return this.storeCollection(storeId, FirebaseCollections.Maintenance)
  },

  inspectionEndorsementSlug(make: string, model: string, component: string, type: string) {
    const norm = (s: string) =>
      s
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
    return `${norm(make)}_${norm(model)}_${norm(component)}_${norm(type)}`
  },

  /** StoresCustomers/{school}/Maintenance/InspectionEndorsements/Templates */
  inspectionEndorsementCollection(storeId: string) {
    return `${this.maintenanceArea(storeId)}/${FirebaseCollections.InspectionEndorsements}/${FirebaseCollections.Templates}`
  },

  inspectionEndorsementDocument(
    storeId: string,
    make: string,
    model: string,
    component: string,
    type: string
  ) {
    return `${this.inspectionEndorsementCollection(storeId)}/${this.inspectionEndorsementSlug(make, model, component, type)}`
  }
}

export const FirestoreRefs = {
  accountDoc(id: string) {
    return doc(db, FirestorePaths.rootDocument(FirebaseCollections.Accounts, id)).withConverter(
      AccountConverter
    )
  }
}
